import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CircleUser, Loader2, Package, MapPin, Save, Truck, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useCustomer } from '../lib/customerContext';
import { customerAuth } from '../lib/api';

const STATUS_META = {
  upcoming: { label: 'Cart / Not placed', chip: 'bg-neutral-100 text-neutral-700 border-neutral-300', step: 0 },
  pending: { label: 'Order Placed', chip: 'bg-amber-100 text-amber-800 border-amber-300', step: 1 },
  packed: { label: 'Packed', chip: 'bg-blue-100 text-blue-800 border-blue-300', step: 2 },
  ready_for_dispatch: { label: 'Ready for Dispatch', chip: 'bg-indigo-100 text-indigo-800 border-indigo-300', step: 3 },
  completed: { label: 'Completed / Shipped', chip: 'bg-emerald-100 text-emerald-800 border-emerald-300', step: 4 },
  cancelled: { label: 'Cancelled', chip: 'bg-red-100 text-red-700 border-red-300', step: -1 },
};
const TRACK_STEPS = ['pending', 'packed', 'ready_for_dispatch', 'completed'];
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return iso || ''; } };

export const MyAccountDialog = ({ open, onOpenChange, whatsappNumber }) => {
  const { customer, updateProfile } = useCustomer();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    if (open && customer) {
      setForm({
        name: customer.name || '', phone: customer.phone || '', alternate_phone: customer.alternate_phone || '',
        address: customer.address || '', pincode: customer.pincode || '', gst_number: customer.gst_number || '',
      });
      loadOrders();
    }
  }, [open, customer]);

  const loadOrders = async () => {
    setLoadingOrders(true);
    try { setOrders(await customerAuth.orders()); } catch { /* ignore */ } finally { setLoadingOrders(false); }
  };

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try { await updateProfile(form); toast.success('Profile updated'); }
    catch { toast.error('Update failed'); } finally { setSaving(false); }
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="my-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CircleUser className="w-5 h-5 text-orange-500" /> My Account
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="profile" data-testid="account-tab-profile">My Details</TabsTrigger>
            <TabsTrigger value="orders" data-testid="account-tab-orders">Order History</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-2.5 pt-3 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2"><Label className="text-[12px] text-neutral-600">Full name</Label><Input data-testid="account-name-input" value={form.name} onChange={f('name')} className="mt-1" /></div>
              <div><Label className="text-[12px] text-neutral-600">Phone</Label><Input data-testid="account-phone-input" value={form.phone} onChange={f('phone')} className="mt-1" /></div>
              <div><Label className="text-[12px] text-neutral-600">Alternate phone</Label><Input data-testid="account-altphone-input" value={form.alternate_phone} onChange={f('alternate_phone')} className="mt-1" /></div>
              <div className="col-span-2"><Label className="text-[12px] text-neutral-600">Email (cannot change)</Label><Input value={customer.email} disabled className="mt-1 bg-neutral-100" /></div>
              <div className="col-span-2"><Label className="text-[12px] text-neutral-600">Delivery address</Label><Input data-testid="account-address-input" value={form.address} onChange={f('address')} className="mt-1" /></div>
              <div><Label className="text-[12px] text-neutral-600">Pincode</Label><Input data-testid="account-pincode-input" value={form.pincode} onChange={f('pincode')} className="mt-1" /></div>
              <div><Label className="text-[12px] text-neutral-600">GST number</Label><Input data-testid="account-gst-input" value={form.gst_number} onChange={f('gst_number')} className="mt-1" /></div>
            </div>
            <Button data-testid="account-save-button" onClick={save} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save details</>}
            </Button>
          </TabsContent>

          <TabsContent value="orders" className="pt-3 max-h-[60vh] overflow-y-auto">
            {loadingOrders ? (
              <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-neutral-400" /></div>
            ) : orders.length === 0 ? (
              <div className="py-8 text-center text-[13px] text-neutral-500"><Package className="w-8 h-8 mx-auto mb-2 text-neutral-300" />Abhi koi order nahi. Products add karke order place karein.</div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => {
                  const meta = STATUS_META[o.status] || STATUS_META.pending;
                  return (
                    <div key={o.id} data-testid={`account-order-${o.id}`} className="border border-neutral-200 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold text-[13px]">{o.product_name || 'Order'}</span>
                        <span data-testid={`account-order-status-${o.id}`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.chip}`}>{meta.label}</span>
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">{fmtDate(o.created_at)} · {o.total_qty} pcs · ₹{(o.grand_total || 0).toLocaleString('en-IN')}</div>

                      {o.status !== 'cancelled' && (
                        <div className="flex items-center gap-1 mt-2" data-testid={`account-order-tracker-${o.id}`}>
                          {TRACK_STEPS.map((s, idx) => {
                            const done = meta.step >= STATUS_META[s].step;
                            return (
                              <React.Fragment key={s}>
                                <div className={`flex-1 h-1.5 rounded-full ${done ? 'bg-orange-500' : 'bg-neutral-200'}`} />
                                {idx < TRACK_STEPS.length - 1 && <span className="w-0.5" />}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                        <span>Placed</span><span>Packed</span><span>Dispatch</span><span>Shipped</span>
                      </div>

                      {o.tracking_number ? (
                        <div className="mt-2 text-[12px] flex items-center gap-1 text-indigo-700"><Truck className="w-3.5 h-3.5" /> Tracking: <b>{o.tracking_number}</b></div>
                      ) : null}

                      {(o.items || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {o.items.map((it, i) => <span key={i} className="text-[10px] bg-neutral-50 border border-neutral-200 rounded px-1.5 py-0.5">{it.color} / {it.size} — <b>{it.qty}</b></span>)}
                        </div>
                      )}

                      {o.customization ? <div className="mt-2 text-[11px] text-neutral-600"><b>Customisation:</b> {o.customization}</div> : null}

                      {whatsappNumber && (
                        <a
                          data-testid={`account-order-customize-${o.id}`}
                          href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hi, regarding my order of ${o.product_name} (${o.total_qty} pcs) — I'd like to discuss customisation/printing.`)}`}
                          target="_blank" rel="noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 h-7 px-2.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold"
                        >
                          <MessageCircle className="w-3 h-3" /> Request customisation
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default MyAccountDialog;
